// ============================================================
// BillableTimeCard.tsx
// "Billable time" — the dashboard's time chart, in the approved mockup's
// design (casevo-dashboard-premium.html, option 2 — the original
// TimeRecordedCard): viewBox 720×220 · L52 R12 T16 B34 (billableChartLayout.ts;
// L wider than the mockup's 40 so "2h 30m" fits) · the days INSET half a slot
// from each axis: x = L + ½·STEP + i·STEP, STEP = (W−L−R)/7 (xOf)
// · three gridlines (0, ½, max) · area gradient #40916c 35% → 0 · stroke
// #2d6a4f 2.5 round · future days shaded (track, 45%) · gold dashed "now"
// line + gold ring AROUND today's solid r4 dot · r4 dots on recorded days ·
// every line (this week's and the comparison's) leads in from 0 at the
// plot's left edge, the start of Monday · a lone point (Monday so far) also
// gets its value beside it · "Today" label
// #8a6d18 (was #b28a1f — 3.2:1, below AA at 12px). Card radius 16 · pad 24/26/14 · big total 38px/600 (the "h" at
// 19px) · delta chip 12px/600 pill. The ONE change from the mockup: STRAIGHT
// (linear) segments between days instead of the cubic smoothing.
//
// Data: MY OWN billable time per LOCAL day (Mon–Sun). Billable = the
// server's `billable` flag on each GET /api/activities row: billing's own
// isBillableEntry rule (billable type, chargeable time) AND on a case — so
// case-less time and non-billable types are excluded. "Recorded" = every
// entry of mine that day. Hours only — no £, so the money visibility rules
// don't come into play. Per-day tooltip: "2h 30m billable · 3h recorded".
// On the current week the line stops at today.
//
// Comparison + figures (dashboard-chart-mockups: direction A + B's figures):
//   • a faint DASHED comparison line —
//       – on THIS week: last week (all seven days), from the previous week's
//         entries the same fetch already returns;
//       – on a PAST week: this week so far (it stops at today), from a
//         second, parallel fetch of the current week;
//   • a LIKE-FOR-LIKE chip, the same span in both weeks: Monday up to
//     TODAY's weekday ("+14% vs same point last week" / "−8% vs this week"),
//     so a whole past week is never compared with a part-finished week;
//   • a crosshair tooltip that adds the comparison week's figure for that day
//     ("Last Fri 16 Oct: …" / "This week, Fri 23 Oct: …");
//   • three figures under the total: average per working day (Mon–Fri
//     days elapsed, today included), best day, and % billable
//     (billable ÷ recorded).
//
// Week paging (‹ / › / "This week", in the header beside the delta chip):
// the shown week is part of the dashboard's place (usePlaceParam 'week' =
// the Monday's YYYY-MM-DD; absent = this week), so paging REPLACES the
// current history entry — like calendar paging, no extra Back step. This
// week: ONE fetch, [previous Monday, this Sunday 23:59:59.999]. A past week:
// two in parallel — that week, and this week (the comparison).
// ============================================================
import { useLivePermissions } from '../../hooks/useLivePermissions'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { listActivitiesApi } from '../../api/casevoApi'
import { initialPlaceParam, usePlaceParam } from '../../navigation/navigate'
import { presence } from '../../services/presence'
import { ymdLocal } from '../tasks/myWorkLogic'
import type { Activity } from '../../types/activity.types'
import {
  BASE, CMP_DOT_R, CMP_LABEL_SIZE, DOT_R, H, L, NOW_RING_R, NOW_RING_STROKE, PLOT_BLEED, R, STEP, T, VALUE_LABEL_SIZE, W,
  axisCeiling, cmpLabelY, placeTooltip, seriesArea, seriesPath, singlePointLabel, xOf, yOf as yAt,
} from './billableChartLayout'
import type { TipPlacement } from './billableChartLayout'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

// Local Monday 00:00 of the week containing `d`.
const mondayOf = (d: Date): Date => {
  const m = new Date(d)
  m.setHours(0, 0, 0, 0)
  m.setDate(m.getDate() - ((m.getDay() + 6) % 7))
  return m
}
const addDays = (d: Date, n: number): Date => {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}
// 'YYYY-MM-DD' → local Monday of that week; anything else → null.
const parseWeek = (v: string | null): Date | null => {
  if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return null
  const [y, m, d] = v.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  return Number.isNaN(dt.getTime()) ? null : mondayOf(dt)
}

// "13–19 Oct" · "29 Sep – 5 Oct" · "29 Dec 2025 – 4 Jan 2026" (year only
// when the week isn't in the current year).
const weekRangeLabel = (mon: Date): string => {
  const sun = addDays(mon, 6)
  const thisYear = new Date().getFullYear()
  if (mon.getFullYear() !== sun.getFullYear()) {
    return `${mon.getDate()} ${MONTHS[mon.getMonth()]} ${mon.getFullYear()} – ${sun.getDate()} ${MONTHS[sun.getMonth()]} ${sun.getFullYear()}`
  }
  const year = sun.getFullYear() !== thisYear ? ` ${sun.getFullYear()}` : ''
  if (mon.getMonth() === sun.getMonth()) return `${mon.getDate()}–${sun.getDate()} ${MONTHS[sun.getMonth()]}${year}`
  return `${mon.getDate()} ${MONTHS[mon.getMonth()]} – ${sun.getDate()} ${MONTHS[sun.getMonth()]}${year}`
}

// "0m" / "15m" / "1h" / "2h 30m".
const formatDuration = (seconds: number): string => {
  const totalMin = Math.round(seconds / 60)
  if (totalMin <= 0) return '0m'
  const hh = Math.floor(totalMin / 60)
  const mm = totalMin % 60
  if (hh === 0) return `${mm}m`
  if (mm === 0) return `${hh}h`
  return `${hh}h ${mm}m`
}

// Axis tick label with units: "0h" · "15m" · "1h 30m" · "4h".
const tickLabel = (hours: number): string => (hours === 0 ? '0h' : formatDuration(hours * 3600))

// The dashed comparison series. On this week it's LAST week (seven days that
// have happened); on a past week it's THIS week, so far (days = today + 1).
interface Comparison {
  kind: 'last' | 'this'
  mon: Date // the comparison week's Monday
  billable: number[] // seconds per day, Mon..Sun
  days: number // days of it that have happened (7, or today's index + 1)
}

interface WeekData {
  billable: number[] // seconds per day, Mon..Sun
  recorded: number[]
  cmp: Comparison
}

// One week's entries per LOCAL day — only rows inside [mon, mon + 7 days).
const bucketWeek = (activities: Activity[], mon: Date): { billable: number[]; recorded: number[] } => {
  const billable = [0, 0, 0, 0, 0, 0, 0]
  const recorded = [0, 0, 0, 0, 0, 0, 0]
  const from = mon.getTime()
  const to = addDays(mon, 7).getTime()
  for (const a of activities) {
    if (!a.createdAt) continue
    const d = new Date(a.createdAt)
    const t = d.getTime()
    if (Number.isNaN(t) || t < from || t >= to) continue
    const secs = a.durationSeconds > 0 ? a.durationSeconds : 0
    const idx = (d.getDay() + 6) % 7
    recorded[idx] += secs
    if (a.billable) billable[idx] += secs
  }
  return { billable, recorded }
}

const sumTo = (xs: number[], upto: number): number => xs.slice(0, upto + 1).reduce((s, v) => s + v, 0)
// "Mon" · "Mon–Fri" — the like-for-like span named in the chip's tooltip.
const spanLabel = (upto: number): string => (upto === 0 ? 'Mon' : `Mon–${DAY_LABELS[upto]}`)

const ChevronIcon: React.FC<{ up: boolean }> = ({ up }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points={up ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
  </svg>
)
const ArrowIcon: React.FC<{ dir: 'left' | 'right' }> = ({ dir }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points={dir === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
  </svg>
)

// ── The chart (mockup areaChart(), linear) ──────────────────
// Geometry (viewBox, margins, axis ceiling, label + tooltip placement) lives
// in billableChartLayout.ts.

// `card` is the card the tooltip is portalled into: it's positioned against
// the card (not the chart, whose wrapper clips), kept fully inside it.
const AreaChart: React.FC<{ data: WeekData; mon: Date; today: number | null; card: HTMLElement | null }> = ({ data, mon, today, card }) => {
  const [hover, setHover] = useState<number | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const [tipPos, setTipPos] = useState<TipPlacement | null>(null)
  const hours = data.billable.map((s) => s / 3600)
  const cmp = data.cmp
  const cmpHours = cmp.billable.slice(0, cmp.days).map((s) => s / 3600)
  // One axis for BOTH weeks (the higher of the two, plus headroom, rounded to
  // a tidy gridline), so neither line ever leaves the plot.
  const max = axisCeiling(hours, cmpHours)
  const xs = DAY_LABELS.map((_, i) => xOf(i))
  const yOf = (v: number): number => yAt(v, max)
  const base = BASE

  // Points up to and including today on the current week (the rest hasn't
  // happened); all seven on a past week. Straight segments between them,
  // leading in from zero at the plot's left edge (seriesPath).
  const last = today ?? 6
  const pts: [number, number][] = xs.slice(0, last + 1).map((x, i) => [x, yOf(hours[i])])
  const d = seriesPath(pts.map(([, y]) => y))
  const area = seriesArea(pts.map(([, y]) => y))
  const futureX = today !== null && today < 6 ? xs[today] + STEP * 0.5 : null
  // Only one day so far → its value beside it ("36m").
  const lone = pts.length === 1 && hours[0] > 0 ? singlePointLabel(pts[0][0], pts[0][1]) : null
  // The comparison week — only the days of it that have happened (all seven
  // for last week; up to today for this week), leading in from zero too.
  const cmpD = seriesPath(cmpHours.map((h) => yOf(h)))
  const hasCmp = cmpHours.some((h) => h > 0)
  const cmpEnd = cmpHours.length - 1
  const cmpName = cmp.kind === 'last' ? 'Last week' : 'This week'

  const tip = (i: number): string => `${formatDuration(data.billable[i])} billable · ${formatDuration(data.recorded[i])} recorded`
  const dayName = (i: number): string => {
    const dt = addDays(mon, i)
    return `${DAY_LABELS[i]} ${dt.getDate()} ${MONTHS[dt.getMonth()]}`
  }
  // The tooltip's comparison line: "Last Fri 16 Oct: 4h billable" (this week)
  // · "This week, Fri 23 Oct: 2h 30m billable" (a past week; "not yet" for a
  // day of this week still to come).
  const cmpTip = (i: number): string => {
    const dt = addDays(cmp.mon, i)
    const day = `${DAY_LABELS[i]} ${dt.getDate()} ${MONTHS[dt.getMonth()]}`
    if (cmp.kind === 'last') return `Last ${day}: ${formatDuration(cmp.billable[i])} billable`
    return i < cmp.days ? `This week, ${day}: ${formatDuration(cmp.billable[i])} billable` : `This week, ${day}: not yet`
  }

  // Place the tooltip before paint: the day's points → card px (via the SVG's
  // own transform, so letterboxing from preserveAspectRatio is accounted
  // for), then above / flipped below and clamped inside the card.
  useLayoutEffect(() => {
    const svg = svgRef.current
    const tipEl = tipRef.current
    const m = svg?.getScreenCTM()
    if (hover === null || !svg || !tipEl || !card || !m) { setTipPos(null); return }
    const box = card.getBoundingClientRect()
    const ys = [yOf(hours[hover])]
    if (hasCmp && hover < cmp.days) ys.push(yOf(cmpHours[hover]))
    const toY = (y: number): number => m.d * y + m.f - box.top
    setTipPos(placeTooltip({
      x: m.a * xs[hover] + m.e - box.left,
      topY: toY(Math.min(...ys)),
      bottomY: toY(Math.max(...ys)),
      tipW: tipEl.offsetWidth,
      tipH: tipEl.offsetHeight,
      boxW: box.width,
      boxH: box.height,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hover, card, data])

  // The comparison's direct label: above / below its last point — or, when
  // that's its ONLY point (a past week viewed on a Monday), beside its dot.
  const cmpLone = hasCmp && cmpEnd === 0
  const cmpLabel = !hasCmp ? null
    : cmpLone ? { ...singlePointLabel(xs[0], yOf(cmpHours[0]), CMP_DOT_R + 1, CMP_LABEL_SIZE), anchor: 'start' as const }
      : { x: xs[cmpEnd], y: cmpLabelY(yOf(cmpHours[cmpEnd])), anchor: 'end' as const }

  return (
    <div className="relative">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="block w-full h-auto sm:h-[220px]" role="img"
        aria-label={`Billable time by day: ${DAY_LABELS.map((_, i) => `${dayName(i)} ${tip(i)}`).join('; ')}. ${cmpName}${cmp.kind === 'this' ? ' so far' : ''}: ${DAY_LABELS.slice(0, cmp.days).map((w, i) => `${w} ${formatDuration(cmp.billable[i])}`).join(', ')}`}>
        <defs>
          <linearGradient id="casevo-billable-area" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#40916c" stopOpacity=".35" />
            <stop offset="1" stopColor="#40916c" stopOpacity="0" />
          </linearGradient>
          {/* The plot area (+ room for a dot's radius) — series never draw past it. */}
          <clipPath id="casevo-billable-plot">
            <rect x={L - PLOT_BLEED} y={T - PLOT_BLEED} width={W - R - L + 2 * PLOT_BLEED} height={base - T + 2 * PLOT_BLEED} />
          </clipPath>
        </defs>
        {[0, max / 2, max].map((v) => {
          const y = yOf(v)
          return (
            <g key={v}>
              <line x1={L} x2={W - R} y1={y} y2={y} className="stroke-casevo-border-soft" strokeDasharray={v ? '3 5' : undefined} />
              <text x={L - 10} y={y + 4} textAnchor="end" fontSize="11" fontFamily="DM Sans, sans-serif" className="fill-[#6b7280] dark:fill-[#8aa899]">
                {tickLabel(v)}
              </text>
            </g>
          )
        })}
        {futureX !== null && (
          <rect x={futureX} y={T} width={W - R - futureX} height={base - T} className="fill-[#e8f1ec] dark:fill-[#31443a]" opacity=".45" />
        )}
        {/* Comparison week — faint, dashed, direct-labelled at its last point
            ("Last week" / "This week"), so identity is never colour-alone. */}
        <g clipPath="url(#casevo-billable-plot)">
        {hasCmp && (
          <path d={cmpD} fill="none" className="stroke-[#7f958a] dark:stroke-[#7a817d]" strokeWidth="2" strokeDasharray="5 4" strokeLinecap="round" strokeLinejoin="round" />
        )}
        {cmpLone && hover !== 0 && (
          <circle cx={xs[0]} cy={yOf(cmpHours[0])} r={CMP_DOT_R} className="fill-white dark:fill-[#26352c] stroke-[#7f958a] dark:stroke-[#7a817d]" strokeWidth="2" />
        )}
        {area && <path d={area} fill="url(#casevo-billable-area)" />}
        <path d={d} fill="none" stroke="#2d6a4f" className="stroke-[#2d6a4f] dark:stroke-[#74c69d]" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Crosshair: the day's column line + the comparison week's point for that day. */}
        {hover !== null && hover <= last && (
          <>
            <line x1={xs[hover]} x2={xs[hover]} y1={T} y2={base} className="stroke-casevo-border" />
            {hasCmp && hover < cmp.days && (
              <circle cx={xs[hover]} cy={yOf(cmpHours[hover])} r="4" className="fill-white dark:fill-[#26352c] stroke-[#7f958a] dark:stroke-[#7a817d]" strokeWidth="2" />
            )}
          </>
        )}
        {today !== null && (
          <line x1={xs[today]} x2={xs[today]} y1={T} y2={base} stroke="#e0b64a" strokeDasharray="3 4" />
        )}
        {/* Today always has its solid dot (like every recorded day's); the
            gold Now ring is drawn AROUND it. */}
        {pts.map(([x, y], i) => (i === today || hours[i] > 0 || i === hover
          ? <circle key={i} cx={x} cy={y} r={i === hover ? DOT_R + 1 : DOT_R} fill="#2d6a4f" className="fill-[#2d6a4f] dark:fill-[#74c69d]" />
          : null))}
        {today !== null && (
          <circle cx={xs[today]} cy={yOf(hours[today])} r={NOW_RING_R} fill="none" stroke="#e0b64a" strokeWidth={NOW_RING_STROKE} />
        )}
        </g>
        {/* The comparison line's direct label — above its last point, or below
            it near the top (cmpLabelY); drawn last so it sits over the lines. */}
        {cmpLabel && (
          <text x={cmpLabel.x} y={cmpLabel.y} textAnchor={cmpLabel.anchor} fontSize={CMP_LABEL_SIZE} fontWeight={600} fontFamily="DM Sans, sans-serif"
            className="fill-[#7f958a] dark:fill-[#7a817d] stroke-white dark:stroke-[#26352c]" style={{ paintOrder: 'stroke', strokeWidth: 4, strokeLinejoin: 'round' }}>
            {cmpName}
          </text>
        )}
        {lone && (
          <text x={lone.x} y={lone.y} fontSize={VALUE_LABEL_SIZE} fontWeight={600} fontFamily="DM Sans, sans-serif"
            className="fill-[#2d6a4f] dark:fill-[#74c69d] stroke-white dark:stroke-[#26352c] tabular-nums" style={{ paintOrder: 'stroke', strokeWidth: 4, strokeLinejoin: 'round' }}>
            {formatDuration(data.billable[0])}
          </text>
        )}
        {DAY_LABELS.map((w, i) => (
          <text key={w} x={xs[i]} y={H - 10} textAnchor="middle" fontSize="12" fontFamily="DM Sans, sans-serif"
            fontWeight={i === today ? 700 : 400}
            className={i === today ? 'fill-[#8a6d18] dark:fill-[#e0b64a]' : 'fill-[#6b7280] dark:fill-[#8aa899]'}>
            {i === today ? 'Today' : w}
          </text>
        ))}
        {/* Hover / focus targets — one column per day (only days that have happened). */}
        {xs.slice(0, last + 1).map((x, i) => (
          <rect key={i} x={x - STEP / 2} y={T} width={STEP} height={H - T} fill="transparent"
            tabIndex={0} role="img" aria-label={`${dayName(i)}: ${tip(i)}${hasCmp ? `. ${cmpTip(i)}` : ''}`} className="outline-none"
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover((h) => (h === i ? null : h))}
            onFocus={() => setHover(i)} onBlur={() => setHover((h) => (h === i ? null : h))} />
        ))}
      </svg>
      {/* Portalled into the card (the chart's wrapper clips), placed by the
          layout effect; hidden until it has been measured. w-max + max-w lets
          a long line wrap on a narrow card instead of running off it. */}
      {hover !== null && card && createPortal(
        <div ref={tipRef} role="tooltip"
          className="pointer-events-none absolute z-10 w-max max-w-[calc(100%-16px)] rounded-lg border border-casevo-border bg-casevo-surface px-2.5 py-1.5 text-[12px] shadow-[0_6px_18px_-6px_rgba(16,40,28,0.25)]"
          style={tipPos ? { left: tipPos.left, top: tipPos.top } : { left: 0, top: 0, visibility: 'hidden' }}>
          <div className="font-semibold text-casevo-forest">{dayName(hover)}</div>
          <div className="text-casevo-text-soft tabular-nums">{tip(hover)}</div>
          {hasCmp && (
            <div className="mt-0.5 pt-[3px] border-t border-casevo-border-soft flex items-center gap-1.5 text-casevo-text-soft tabular-nums">
              <span className="shrink-0 w-4 border-t-2 border-dashed border-[#7f958a] dark:border-[#7a817d]" aria-hidden="true" />{cmpTip(hover)}
            </div>
          )}
        </div>,
        card,
      )}
    </div>
  )
}

const CARD = 'rounded-[14px] border border-casevo-border bg-casevo-surface shadow-[0_1px_2px_rgba(16,40,28,0.05),0_2px_6px_rgba(16,40,28,0.04)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]'
const LBL = 'text-[11.5px] tracking-[0.14em] uppercase font-semibold text-casevo-brand dark:text-[#74c69d]'
// Week paging — a small pill group in the delta chip's idiom (12px/600, round).
const PAGER = 'inline-flex items-center rounded-full border border-casevo-border bg-casevo-surface text-casevo-forest text-[12px] font-semibold'
const PAGE_BTN = 'inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-[#e3f2ea] dark:hover:bg-[rgba(127,209,168,0.12)] transition-colors disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid'

const BillableTimeCard: React.FC = () => {
  const currentMon = mondayOf(new Date())
  const [mon, setMon] = useState<Date>(() => {
    const p = parseWeek(initialPlaceParam('week'))
    return p && p.getTime() <= currentMon.getTime() ? p : currentMon
  })
  const isCurrent = mon.getTime() === currentMon.getTime()
  const monKey = ymdLocal(mon)
  usePlaceParam('week', isCurrent ? null : monKey, (v) => setMon(parseWeek(v) ?? mondayOf(new Date())))

  const [state, setState] = useState<{ key: string; data: WeekData | null; error: boolean } | null>(null)
  const [nonce, setNonce] = useState(0)
  // ★ Live permission change: drop the figures at once if a money right was
  // lost, then re-read.
  useLivePermissions((narrowed) => { if (narrowed) setState(null); setNonce((n) => n + 1) })
  // The card element — the chart's tooltip is portalled into it.
  const [cardEl, setCardEl] = useState<HTMLElement | null>(null)

  // A change to someone's time (e.g. an admin editing one of mine) → refetch.
  useEffect(() => {
    let timer: number | undefined
    const unsub = presence.subscribeEntityChanged((e) => {
      if (e.entity !== 'activity') return
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setNonce((n) => n + 1), 1500)
    })
    return () => { unsub(); window.clearTimeout(timer) }
  }, [])

  useEffect(() => {
    let cancelled = false
    const weekEnd = (m: Date): Date => { const e = addDays(m, 6); e.setHours(23, 59, 59, 999); return e }
    const fetchRange = (from: Date, to: Date): Promise<Activity[]> =>
      listActivitiesApi({ actorUserId: 'me', from: from.toISOString(), to: to.toISOString() })
        .then((res) => (Array.isArray(res?.activities) ? res.activities : []))
    const nowMon = mondayOf(new Date())
    const todayIdx = (new Date().getDay() + 6) % 7
    const viewingCurrent = mon.getTime() === nowMon.getTime()
    // This week: one call covers it AND last week. A past week: that week +
    // this week (the comparison), in parallel.
    const load: Promise<WeekData> = viewingCurrent
      ? fetchRange(addDays(mon, -7), weekEnd(mon)).then((acts) => ({
          ...bucketWeek(acts, mon),
          cmp: { kind: 'last' as const, mon: addDays(mon, -7), billable: bucketWeek(acts, addDays(mon, -7)).billable, days: 7 },
        }))
      : Promise.all([fetchRange(mon, weekEnd(mon)), fetchRange(nowMon, weekEnd(nowMon))]).then(([acts, nowActs]) => ({
          ...bucketWeek(acts, mon),
          cmp: { kind: 'this' as const, mon: nowMon, billable: bucketWeek(nowActs, nowMon).billable, days: todayIdx + 1 },
        }))
    load
      .then((data) => { if (!cancelled) setState({ key: monKey, data, error: false }) })
      .catch(() => { if (!cancelled) setState({ key: monKey, data: null, error: true }) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monKey, nonce])

  const loading = !state || state.key !== monKey
  const data = !loading ? state.data : null
  const error = !loading && state.error

  const total = useMemo(() => (data ? data.billable.reduce((s, v) => s + v, 0) : 0), [data])
  const recordedTotal = useMemo(() => (data ? data.recorded.reduce((s, v) => s + v, 0) : 0), [data])
  // Today's weekday (0 = Mon) — the like-for-like span's end in BOTH views.
  const todayIdx = (new Date().getDay() + 6) % 7
  const today = isCurrent ? todayIdx : null
  const cmpTotal = useMemo(() => (data ? sumTo(data.cmp.billable, data.cmp.days - 1) : 0), [data])

  // LIKE-FOR-LIKE delta: the shown week vs the comparison week over the SAME
  // span — Monday up to today's weekday — in both views. This week vs last
  // week Mon→today; a past week vs this week Mon→today (never a whole past
  // week against a part-finished one). No chip when the comparison span is
  // zero, or the two are equal.
  const delta = useMemo(() => {
    if (!data) return null
    const cur = sumTo(data.billable, todayIdx)
    const other = sumTo(data.cmp.billable, todayIdx)
    if (other <= 0 || cur === other) return null
    return { up: cur > other, pct: Math.round((Math.abs(cur - other) / other) * 100) }
  }, [data, todayIdx])

  // Direction B's figures. Average per WORKING day = Mon–Fri billable ÷ the
  // Mon–Fri days elapsed (today included; a past week → 5). Best day = the
  // day (up to today) with the most billable time. % billable = billable ÷
  // recorded for the week.
  const figures = useMemo(() => {
    if (!data) return null
    const workDays = today === null ? 5 : Math.min(today + 1, 5)
    const weekdayBillable = data.billable.slice(0, 5).reduce((s, v) => s + v, 0)
    let best: number | null = null
    for (let i = 0; i <= (today ?? 6); i++) if (data.billable[i] > 0 && (best === null || data.billable[i] > data.billable[best])) best = i
    return {
      avg: weekdayBillable / workDays,
      workDays,
      best,
      bestSecs: best !== null ? data.billable[best] : 0,
      rate: recordedTotal > 0 ? Math.round((total / recordedTotal) * 100) : null,
    }
  }, [data, today, total, recordedTotal])
  const bestDayLabel = (i: number): string => {
    const dt = addDays(mon, i)
    return `${DAY_LABELS[i]} ${dt.getDate()} ${MONTHS[dt.getMonth()]}`
  }
  const figureCells: { k: string; v: string; sub: string }[] = [
    { k: 'Avg / working day', v: figures && total > 0 ? formatDuration(figures.avg) : '—',
      sub: figures ? (isCurrent ? `over ${figures.workDays} day${figures.workDays > 1 ? 's' : ''} so far` : 'Mon–Fri') : '' },
    { k: 'Best day', v: figures && figures.best !== null ? formatDuration(figures.bestSecs) : '—',
      sub: figures ? (figures.best !== null ? bestDayLabel(figures.best) : 'No billable time') : '' },
    { k: 'Billable', v: figures && figures.rate !== null ? `${figures.rate}%` : '—',
      sub: figures ? (recordedTotal > 0 ? `of ${formatDuration(recordedTotal)} recorded` : 'Nothing recorded') : '' },
  ]
  const range = weekRangeLabel(mon)

  return (
    <section ref={setCardEl} className={`${CARD} relative px-5 sm:px-[26px] pt-6 pb-3.5 min-w-0`} aria-busy={loading || undefined}>
      <div className="flex items-start gap-4">
        <div className="min-w-0">
          <h2 className={LBL}>Billable time</h2>
          <div className="mt-2.5 leading-none tracking-[-0.02em] text-casevo-forest tabular-nums">
            {loading
              ? <span className="inline-block align-bottom h-[38px] w-28 rounded-md bg-[#e8f1ec] dark:bg-[#31443a] animate-pulse motion-reduce:animate-none" />
              : <>
                  <span className="text-[38px] font-semibold">{error ? '—' : (total / 3600).toFixed(1)}</span>
                  {!error && <span className="text-[19px] font-semibold">h</span>}
                </>}{' '}
            <span className="inline-block text-[14px] font-medium text-casevo-text-soft tracking-normal">{isCurrent ? 'this week' : 'that week'} · {range}</span>
          </div>
        </div>
        <span className="flex-1" />
        <div className="shrink-0 flex flex-col items-end gap-2">
          <div className="flex items-center gap-1.5">
            {!isCurrent && (
              <button type="button" onClick={() => setMon(currentMon)}
                className={`${PAGER} h-[30px] px-3 hover:bg-[#e3f2ea] dark:hover:bg-[rgba(127,209,168,0.12)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid`}>
                This week
              </button>
            )}
            <div className={`${PAGER} p-px`} role="group" aria-label="Week">
              <button type="button" className={PAGE_BTN} onClick={() => setMon(addDays(mon, -7))} aria-label="Previous week">
                <ArrowIcon dir="left" />
              </button>
              <button type="button" className={PAGE_BTN} onClick={() => setMon(addDays(mon, 7))} disabled={isCurrent}
                aria-label="Next week" title={isCurrent ? 'Future weeks have no time yet' : undefined}>
                <ArrowIcon dir="right" />
              </button>
            </div>
          </div>
          {delta && (
            <span title={isCurrent
              ? `Same point: ${spanLabel(todayIdx)} this week vs ${spanLabel(todayIdx)} last week`
              : `Same point: ${spanLabel(todayIdx)} of ${range} vs ${spanLabel(todayIdx)} this week`}
              className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1 rounded-full tabular-nums ${
              delta.up
                ? 'bg-[#e3f2ea] text-[#2d6a4f] dark:bg-[rgba(127,209,168,0.12)] dark:text-[#7fd1a8]'
                : 'bg-[#fbeae7] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.12)] dark:text-[#f3a094]'
            }`}>
              <ChevronIcon up={delta.up} /> {delta.up ? '+' : '−'}{delta.pct}% {isCurrent ? 'vs same point last week' : 'vs this week'}
            </span>
          )}
        </div>
      </div>
      {/* The three figures under the total (same-size placeholders while loading). */}
      <dl className="mt-4 grid grid-cols-1 min-[520px]:grid-cols-3 rounded-[10px] border border-casevo-border-soft bg-casevo-input dark:bg-black/10">
        {figureCells.map(({ k, v, sub }, i) => (
          <div key={k} className={`px-3.5 py-2.5 min-w-0 ${i ? 'border-t min-[520px]:border-t-0 min-[520px]:border-l border-casevo-border-soft' : ''}`}>
            <dt className="text-[10.5px] tracking-[0.12em] uppercase font-bold text-casevo-text-soft">{k}</dt>
            <dd className="m-0">
              {loading
                ? <span className="block mt-1 mb-px h-[23px] w-16 rounded bg-[#e8f1ec] dark:bg-[#31443a] animate-pulse motion-reduce:animate-none" />
                : <span className="block mt-0.5 text-[18px] leading-[26px] font-semibold text-casevo-forest tabular-nums truncate">{error ? '—' : v}</span>}
              <span className="block text-[12px] leading-[18px] text-casevo-text-soft tabular-nums truncate">{loading || error ? '\u00a0' : sub}</span>
            </dd>
          </div>
        ))}
      </dl>
      <div className="mt-3.5 overflow-hidden">
        {loading || !data
          ? <div className={`h-[220px] rounded-xl bg-[#e8f1ec]/70 dark:bg-[#31443a]/60 ${loading ? 'animate-pulse motion-reduce:animate-none' : ''}`} />
          : <AreaChart data={data} mon={mon} today={today} card={cardEl} />}
      </div>
      <div className="flex flex-wrap items-center gap-[18px] text-[12px] text-casevo-text-soft mt-[9px]">
        {/* Legend: the SOLID line is the shown week, the DASHED one the comparison. */}
        <span className="flex items-center gap-1.5" title="Billable time on cases — excludes case-less and non-billable entries">
          <span className="w-3.5 h-[3px] rounded-sm bg-[#2d6a4f] dark:bg-[#74c69d]" />{isCurrent ? 'This week' : range}
        </span>
        {data && cmpTotal > 0 && (
          <span className="flex items-center gap-1.5 tabular-nums">
            <span className="w-4 border-t-2 border-dashed border-[#7f958a] dark:border-[#7a817d]" />
            {data.cmp.kind === 'last' ? 'Last week' : 'This week so far'} · {formatDuration(cmpTotal)}
          </span>
        )}
        {isCurrent && <span className="flex items-center gap-1.5"><span className="w-[7px] h-[7px] rounded-full bg-casevo-gold" />Now</span>}
        {loading
          ? <span className="ml-auto">Loading…</span>
          : error
            ? <span role="alert" className="ml-auto text-casevo-danger">Couldn't load billable time.</span>
            : total === 0
              ? <span className="ml-auto">{recordedTotal > 0 ? `No billable time · ${formatDuration(recordedTotal)} recorded` : 'No time recorded.'}</span>
              : <span className="ml-auto tabular-nums">{formatDuration(recordedTotal)} recorded in total</span>}
      </div>
    </section>
  )
}

export default BillableTimeCard
